/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Cookie-less Contentful preview secret store (client/browser only).
 *
 * The preview secret arrives once in the `?secret=` of a `/{site}/preview/<slug>`
 * URL. We capture it into this module-level variable (in memory, never persisted),
 * then strip it from the URL bar (`history.replaceState`) so it is not left in the
 * address bar / browser history (TSD: "strip the secret once validated").
 *
 * Consequences of the in-memory model (by design — see docs §5.0/§9d):
 * - Preview survives client-side navigation within the tab (the value stays in
 *   memory) but a FULL page refresh loses it → re-open the Contentful preview link.
 * - The secret is sent to the same-origin proxy on each Preview API call via
 *   `PREVIEW_HEADER_NAME`; the proxy validates it and injects the preview token,
 *   so the token itself never reaches the browser.
 */

let inMemorySecret = '';

/**
 * @returns {string} the captured preview secret, or '' (incl. during SSR).
 */
export function getPreviewSecret() {
  return inMemorySecret;
}

/**
 * @param {string} value
 */
export function setPreviewSecret(value) {
  inMemorySecret = String(value || '');
}

/** Forget the captured secret (used by the banner's "Revoke preview access"). */
export function clearPreviewSecret() {
  inMemorySecret = '';
}

/**
 * Capture `?secret=` from the current URL into memory, then strip it from the URL
 * bar. Idempotent and client-only: on a URL without `?secret=` it just returns the
 * value already in memory (so it survives in-tab navigation).
 *
 * @returns {string} the current in-memory preview secret
 */
export function capturePreviewSecretFromUrl() {
  if (typeof window === 'undefined') {
    return inMemorySecret;
  }

  const params = new URLSearchParams(window.location.search || '');
  const fromUrl = params.get('secret');

  if (fromUrl) {
    inMemorySecret = fromUrl;

    // Strip only the `secret` param; keep the path, hash, and any other params.
    params.delete('secret');

    const qs = params.toString();
    const next = window.location.pathname + (qs ? `?${qs}` : '') + (window.location.hash || '');

    try {
      window.history.replaceState(window.history.state, '', next);
    } catch {
      // replaceState can throw in rare environments; the secret is already captured.
    }
  }

  return inMemorySecret;
}
